import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/api/client";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { ClipboardCheck, X, BellRing } from "lucide-react";
import { toast } from "sonner";
import { chime } from "@/utils/sound";
import { notify } from "@/utils/desktopNotify";
import { fmtDate, todayIstIso } from "@/utils/date";

// Reminder fires between 17:00 and 23:59 IST, if no log filed today.
const REMINDER_HOUR = 17;
const CHECK_EVERY_MS = 60_000; // re-check every minute
const dismissKey = (dateISO) => `lim-log-reminder-dismissed-${dateISO}`;

/** Read the current hour + weekday in Asia/Kolkata (IST) so this reminder
 * fires on Indian office hours regardless of the browser's local timezone. */
function istClock(now = new Date()) {
  const parts = new Intl.DateTimeFormat("en-GB", {
    timeZone: "Asia/Kolkata", weekday: "short", hour12: false, hour: "2-digit",
  }).formatToParts(now);
  const wd = parts.find((p) => p.type === "weekday")?.value; // Mon, Tue, Sat, Sun ...
  const hour = Number(parts.find((p) => p.type === "hour")?.value);
  return { hour, isWeekend: wd === "Sat" || wd === "Sun" };
}

/** Floating daily-log reminder — nudges users at 5 PM to file their log. */
export default function DailyLogReminder() {
  const { user } = useAuth();
  const nav = useNavigate();
  const [show, setShow] = useState(false);
  const notifiedRef = useRef(false); // OS-level notification & sound fires once per session

  useEffect(() => {
    if (!user) return;
    let alive = true;

    const check = async () => {
      if (!alive) return;
      const now = new Date();
      const dateISO = todayIstIso(now);
      const { hour, isWeekend } = istClock(now);
      // Weekly off (Sat/Sun) → don't nag. If they worked, they can file the log manually to log OT.
      if (isWeekend) { setShow(false); return; }
      // Suppress if user dismissed for today, or the day changed already
      if (sessionStorage.getItem(dismissKey(dateISO)) === "1") { setShow(false); return; }
      // Only nudge after the reminder hour (IST)
      if (hour < REMINDER_HOUR) { setShow(false); return; }

      // Check if today's log exists for this user
      try {
        const { data } = await api.get("/daily-logs", {
          params: { date_from: dateISO, date_to: dateISO },
        });
        const alreadyFiled = Array.isArray(data) && data.some((l) => l.user_id === user.id);
        if (alreadyFiled) { setShow(false); return; }
        setShow(true);
        // Fire the OS/toast reminder once per session
        if (!notifiedRef.current) {
          notifiedRef.current = true;
          chime();
          toast("Time to file your daily log", {
            description: "Wrap up today by logging what you worked on.",
            action: {
              label: "Fill now",
              onClick: () => nav("/daily-log"),
            },
            duration: 12000,
          });
          notify("LIM Flow — Daily-log reminder", {
            body: `Please file today's log (${fmtDate(new Date())}) before you leave.`,
            tag: `lim-daily-log-${dateISO}`,
            url: "/daily-log",
            requireInteraction: true,
          });
        }
      } catch { /* ignore transient errors */ }
    };

    check();
    const id = setInterval(check, CHECK_EVERY_MS);
    return () => { alive = false; clearInterval(id); };
  }, [user, nav]);

  if (!show) return null;

  return (
    <div
      data-testid="daily-log-reminder"
      className="fixed inset-x-0 mx-auto z-50 max-w-md w-[92%] sm:w-fit
                 bottom-36 lg:bottom-24
                 bg-[#E11D2E]
                 border border-[#E11D2E] shadow-float
                 rounded-2xl px-4 py-3 flex items-center gap-3 animate-fade-in-up"
    >
      <div className="grid place-items-center h-9 w-9 rounded-full bg-white/15 shrink-0">
        <BellRing size={18} className="text-white" />
      </div>
      <div className="flex-1 min-w-0">
        <div className="text-sm font-semibold text-white leading-tight">
          Time to file today&apos;s log
        </div>
        <div className="text-xs text-white/80 mt-0.5">
          It powers your KRA and the monthly report.
        </div>
      </div>
      <Button
        size="sm"
        onClick={() => { nav("/daily-log"); }}
        data-testid="daily-log-reminder-jump"
        style={{ background: "#fff", color: "#E11D2E" }}
        className="hover:opacity-90 rounded-full font-semibold h-8 px-3"
      >
        <ClipboardCheck size={13} className="mr-1.5" /> Fill log
      </Button>
      <button
        onClick={() => {
          sessionStorage.setItem(dismissKey(todayIstIso()), "1");
          setShow(false);
        }}
        data-testid="daily-log-reminder-dismiss"
        className="text-white/70 hover:text-white p-1"
        aria-label="Dismiss reminder"
      >
        <X size={14} />
      </button>
    </div>
  );
}
